import React from 'react'

interface LevelBadgeProps {
  level: number
  className?: string
  showLabel?: boolean
  size?: 'sm' | 'md' | 'lg'
  animated?: boolean
}

interface LevelConfig {
  bg: string
  border: string
  text: string
  name: string
  gradient: string
  glow?: boolean
  rainbow?: boolean
}

// Level configuration with unique colors for each level
const getLevelConfig = (level: number): LevelConfig => {
  const configs: Record<number, LevelConfig> = {
    0: {
      bg: 'bg-gray-500',
      border: 'border-gray-600',
      text: 'text-white',
      name: '新手',
      gradient: 'from-gray-400 to-gray-600'
    },
    1: {
      bg: 'bg-amber-800',
      border: 'border-amber-900',
      text: 'text-white',
      name: '初級',
      gradient: 'from-amber-700 to-amber-900'
    },
    2: {
      bg: 'bg-orange-600',
      border: 'border-orange-700',
      text: 'text-white',
      name: '進階',
      gradient: 'from-orange-500 to-orange-700'
    },
    3: {
      bg: 'bg-yellow-600',
      border: 'border-yellow-700',
      text: 'text-white',
      name: '熟練',
      gradient: 'from-yellow-500 to-yellow-700'
    },
    4: {
      bg: 'bg-emerald-600',
      border: 'border-emerald-700',
      text: 'text-white',
      name: '專業',
      gradient: 'from-emerald-500 to-emerald-700'
    },
    5: {
      bg: 'bg-blue-600',
      border: 'border-blue-700',
      text: 'text-white',
      name: '資深',
      gradient: 'from-blue-500 to-blue-700'
    },
    6: {
      bg: 'bg-purple-600',
      border: 'border-purple-700',
      text: 'text-white',
      name: '專家',
      gradient: 'from-purple-500 to-purple-700',
      glow: true
    },
    7: {
      bg: 'bg-pink-600',
      border: 'border-pink-700',
      text: 'text-white',
      name: '大師',
      gradient: 'from-pink-500 to-pink-700',
      glow: true
    },
    8: {
      bg: 'bg-red-600',
      border: 'border-red-700',
      text: 'text-white',
      name: '頂級',
      gradient: 'from-red-500 to-red-700',
      glow: true
    },
    9: {
      bg: 'bg-gradient-to-r from-red-500 via-yellow-500 via-green-500 via-blue-500 to-purple-500',
      border: 'border-purple-500',
      text: 'text-white',
      name: '傳奇',
      gradient: 'from-red-400 via-yellow-400 via-green-400 via-blue-400 to-purple-400',
      glow: true,
      rainbow: true
    }
  }

  return configs[level] || configs[0]
}

const LevelBadge: React.FC<LevelBadgeProps> = ({ 
  level, 
  className = '', 
  showLabel = false, 
  size = 'md',
  animated = false
}) => {
  const config = getLevelConfig(level)
  
  const sizeClasses = {
    sm: 'px-2 py-1 text-xs',
    md: 'px-3 py-1.5 text-sm',
    lg: 'px-4 py-2 text-base'
  }

  const glowClass = config.glow ? 'shadow-lg shadow-current/50' : ''
  const animationClass = animated && config.rainbow ? 'animate-pulse' : ''
  
  return (
    <div className="inline-flex items-center gap-2">
      <span
        className={`
          inline-flex items-center justify-center
          ${sizeClasses[size]}
          ${config.rainbow ? config.bg : `${config.bg} bg-gradient-to-r ${config.gradient}`}
          ${config.border}
          ${config.text}
          ${glowClass}
          ${animationClass}
          border-2 rounded-full font-bold
          transition-all duration-300 hover:scale-110
          ${className}
        `}
        title={`${config.name} (Lv${level})`}
      >
        Lv{level}
      </span>
      
      {showLabel && (
        <span className={`font-medium ${size === 'sm' ? 'text-sm' : size === 'lg' ? 'text-lg' : 'text-base'}`}>
          {config.name}
        </span>
      )}
    </div>
  )
}

// Utility function to get level name
export const getLevelName = (level: number): string => {
  return getLevelConfig(level).name
}

// Utility function to get level color class
export const getLevelColorClass = (level: number): string => {
  const config = getLevelConfig(level)
  return config.rainbow ? 'text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-purple-500' : config.bg.replace('bg-', 'text-')
}

export default LevelBadge
